import { Box, Card, Chip, Stack, Typography } from '@mui/material';

import { radius } from '@karehero/llama/theme/radius';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { SectionTitle } from '@/designs-dev/handover/Panel';
import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ActionCard from '@/components/wrapped/ActionCard';
import OfferCard from '@/components/wrapped/Card';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import { BOOK_CALL } from '@/karehero-app/tell-us-once/intake/content';
import {
  CALLOUTS,
  CONVERSATION,
  FRAMES,
  LABELS,
} from '@/karehero-app/site-mockups/SiteMockups.content';
import {
  ACTIONS,
  CLAIMS,
} from '@/karehero-app/site-mockups/SiteMockups.fixtures';

// Product mockups for the marketing site, built to the language of the three
// references Chloe posted: a flat brand ground, real interface cropped and
// layered on top of it, copy selectively replaced with bars so the picture is
// about the shape of the thing rather than the words in it.
//
// Two departures from the references, both deliberate:
//
// They separate their layers with drop shadows. We don't have those, so the
// layers separate the way the app's own surfaces do — a Marine Green hairline
// on Floral White, against a ground that is neither. It reads as layered and it
// reads as ours, which a shadow would not.
//
// Their highlighted control carries a cursor glyph. A filled ring on its own
// says the same thing without drawing an operating system into a picture of an
// app.
//
// Every fragment is a real component with real props (ActionCard, ChatBubble,
// ClaimRow), so a mockup cannot drift from the app by being redrawn here.

/**
 * The canvas. Fixed size, clipped, and the fragments inside it are positioned
 * absolutely against it so they can bleed off its edges.
 *
 * `ground` is a palette path rather than a colour, so a frame recoloured for a
 * campaign is one token.
 */
export const Frame = ({
  label,
  width,
  height,
  ground,
  children,
}: {
  label: string;
  width: number;
  height: number;
  ground: string;
  children: React.ReactNode;
}) => (
  <Stack spacing={1.5}>
    <SectionTitle label={`${label} · ${width}×${height}`} />
    <Box
      sx={{
        position: 'relative',
        width,
        height,
        flexShrink: 0,
        overflow: 'hidden',
        bgcolor: ground,
        borderRadius: `${radius.md}px`,
      }}
    >
      {children}
    </Box>
  </Stack>
);

/**
 * A fragment, placed. Everything in a frame goes through this, so a composition
 * reads as a list of positions and the maths is in one place.
 */
const Layer = ({
  x,
  y,
  width,
  behind,
  children,
}: {
  /**
   * From the frame's left edge.
   *
   * The references crop their fragments mid-word and get away with it. Ours
   * don't: a claim called "Attendance Allowance" cut to "ttendance Allowance"
   * reads as a broken export rather than as a crop, so everything here sits
   * inside its frame and the layers separate by overlapping each other.
   */
  x: number;
  /** From the frame's top edge. */
  y: number;
  width: number;
  /**
   * Pushed back: the reference washes its background surface out rather than
   * blurring it, which keeps the shapes legible while giving up the foreground.
   */
  behind?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      position: 'absolute',
      left: x,
      top: y,
      width,
      opacity: behind ? 0.45 : 1,
    }}
  >
    {children}
  </Box>
);

/**
 * A bar standing in for a line of copy.
 *
 * The references replace anything that would date the picture, or that the eye
 * does not need, with one of these. Neutral by default; a brand step where the
 * bar is the thing being pointed at.
 */
const Bar = ({
  width,
  height = 12,
  tone = 'custom.neutral.200',
}: {
  width: number | string;
  height?: number;
  tone?: string;
}) => (
  <Box
    sx={{
      width,
      height,
      bgcolor: tone,
      borderRadius: `${radius.sm}px`,
    }}
  />
);

/**
 * A pill naming what it points at, with a leader line up to a ring on the
 * target. Marine Green on Floral White, which is the one place in these
 * pictures type sits on brand rather than on a surface.
 *
 * `lift` is how far above the pill the ring sits. The line is drawn from the
 * pill, so moving the pill moves the whole callout.
 */
const Callout = ({
  x,
  y,
  lift,
  quiet,
  children,
}: {
  x: number;
  y: number;
  lift: number;
  /**
   * Outlined rather than filled: Marine Green on Floral White instead of the
   * other way round.
   *
   * For a composition where the pill lands near a button. A filled pill with a
   * label in it is a button's shape, and two of those in one picture makes the
   * annotation look like a second thing to press.
   */
  quiet?: boolean;
  children: React.ReactNode;
}) => (
  <Box sx={{ position: 'absolute', left: x, top: y }}>
    <Box
      sx={{
        position: 'absolute',
        left: '50%',
        bottom: '100%',
        width: '1px',
        height: lift,
        bgcolor: 'custom.marineGreen.700',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        left: '50%',
        bottom: '100%',
        mb: `${lift - 6}px`,
        ml: '-6px',
        width: 12,
        height: 12,
        bgcolor: 'background.paper',
        border: 2,
        borderColor: 'custom.marineGreen.700',
        borderRadius: '50%',
      }}
    />
    <Box
      sx={{
        px: 3,
        py: 1.5,
        bgcolor: quiet ? 'background.paper' : 'custom.marineGreen.700',
        border: quiet ? 1 : 0,
        borderColor: 'custom.marineGreen.700',
        borderRadius: 999,
      }}
    >
      <Typography
        sx={{
          color: quiet ? 'custom.marineGreen.700' : 'background.paper',
        }}
        variant='h6'
      >
        {children}
      </Typography>
    </Box>
  </Box>
);

/**
 * The ring behind a control the picture is drawing attention to. Sits under the
 * control, so the control keeps its own colour.
 */
const Highlight = ({ size = 56 }: { size?: number }) => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: size,
      height: size,
      bgcolor: 'custom.limeGreen.300',
      borderRadius: '50%',
    }}
  />
);

/** A Floral White surface, which is what every fragment here sits on. */
const Sheet = ({
  children,
  ...rest
}: {
  children: React.ReactNode;
  sx?: object;
}) => (
  <Card sx={{ p: 4, ...rest.sx }}>
    <Stack spacing={2.5}>{children}</Stack>
  </Card>
);

// A header, as bars: an avatar and the two lines beside it. Repeated in three
// compositions, which is what makes it worth naming.
const HeaderBars = () => (
  <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
    <Box
      sx={{
        width: 44,
        height: 44,
        bgcolor: 'custom.limeGreen.500',
        borderRadius: '50%',
        flexShrink: 0,
      }}
    />
    <Stack spacing={1}>
      <Bar width={148} />
      <Bar width={96} height={10} tone='custom.neutral.100' />
    </Stack>
  </Stack>
);

/**
 * A call, on the phone it happens on.
 *
 * The whole screen rather than a fragment: a call is not a card you could crop
 * out of a page, and cropping it would lose the one thing the picture is for —
 * that a person is on the other end of it while you sit with your phone.
 *
 * Drawn from tokens rather than composed from real components, which the other
 * fragments here are not: there is no call screen in the app to crop yet. Keep
 * it to tokens and llama chrome so it cannot invent a look of its own.
 */
const CallScreen = () => (
  <Stack sx={{ height: '100%', bgcolor: 'custom.gardenia' }}>
    <StatusBar />

    <Stack sx={{ flex: 1, minHeight: 0, p: 3 }} spacing={3}>
      {/* The stage. Fills the screen the way a call does, inset far enough to
          read as the app holding it rather than as the video being the app. */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          bgcolor: 'custom.marineGreen.700',
          borderRadius: `${radius.lg}px`,
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Stack sx={{ alignItems: 'center' }} spacing={3}>
          <Box
            sx={{
              width: 96,
              height: 96,
              bgcolor: 'custom.limeGreen.500',
              borderRadius: '50%',
            }}
          />
          <Bar width={120} tone='custom.neutral.100' />
        </Stack>

        {/* Live: a coral dot and the elapsed time, which is what says this is
            happening rather than a call about to be booked. */}
        <Stack
          sx={{ position: 'absolute', left: 16, top: 16, alignItems: 'center' }}
          direction='row'
          spacing={2}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              bgcolor: 'custom.coral.500',
              borderRadius: '50%',
            }}
          />
          <Typography sx={{ color: 'background.paper' }} variant='body2'>
            12:04
          </Typography>
        </Stack>

        {/* The member's own feed: portrait, bottom right, where a phone puts
            it. Floral White rather than a second dark block, so it separates
            from the stage without a shadow. */}
        <Box
          sx={{
            position: 'absolute',
            right: 12,
            bottom: 12,
            width: 92,
            height: 124,
            bgcolor: 'background.paper',
            borderRadius: `${radius.md}px`,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'custom.neutral.200',
              borderRadius: '50%',
            }}
          />
        </Box>
      </Box>

      <Stack sx={{ justifyContent: 'center' }} direction='row' spacing={3}>
        <CallControl
          icon='microphone'
          bg='custom.neutral.100'
          fg='custom.marineGreen.700'
        />
        <CallControl
          icon='video'
          bg='custom.neutral.100'
          fg='custom.marineGreen.700'
        />
        <CallControl
          icon='phone'
          bg='custom.cherry.500'
          fg='custom.neutral.50'
        />
      </Stack>
    </Stack>

    <HomeIndicator />
  </Stack>
);

/**
 * The controls under the stage. Mic and camera neutral, the one that ends the
 * call in Cherry: coral is the colour of the thing the picture wants pressed,
 * and hanging up is not it. Nothing is lit for the same reason — the eye should
 * find the person, not a button.
 */
const CallControl = ({
  icon,
  bg,
  fg,
}: {
  icon: 'microphone' | 'video' | 'phone';
  bg: string;
  fg: string;
}) => (
  <Box
    sx={{
      width: 56,
      height: 56,
      bgcolor: bg,
      color: fg,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
    }}
  >
    <Icon icon={icon} fontSize={20} />
  </Box>
);

// ── the compositions ──────────────────────────────────────────────────────

// Reference 1: a stack of what is included, three fragments deep. The profile
// panel is the thing, the action floats off its left edge, and the claim card
// sits over its top right with the callout under it.
const heroGroup = (
  <Frame {...FRAMES.hero} label={LABELS.hero} ground='custom.limeGreen.200'>
    <Layer x={260} y={120} width={460}>
      <Sheet>
        <HeaderBars />
        <Stack spacing={1.5}>
          {CLAIMS.map((claim) => (
            <ClaimRow
              key={claim.name}
              icon={claim.icon}
              name={claim.name}
              description={claim.description}
            />
          ))}
        </Stack>
      </Sheet>
    </Layer>

    <Layer x={60} y={505} width={400}>
      <ActionCard action={ACTIONS[0]} onToggle={() => {}} onOpen={() => {}} />
    </Layer>

    <Layer x={660} y={50} width={420}>
      <Sheet>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
          spacing={2}
        >
          <Typography variant='h5'>£72.65</Typography>
          <Chip label='a week' color='limeGreen' />
        </Stack>
        <Stack spacing={1}>
          <Bar width='100%' />
          <Bar width='72%' />
        </Stack>
      </Sheet>
    </Layer>

    {/* Same rule as the two-surfaces callout: the amount card bottoms out at
        200 and the pill's top is at 690, so 515 lands the ring 25px inside the
        edge. */}
    <Callout x={690} y={690} lift={515}>
      {CALLOUTS.claim}
    </Callout>
  </Frame>
);

// Reference 3: two surfaces, the member's in front and the care expert's behind
// it, with a wedge of Sunflower in the corner the way the reference puts a
// triangle in its own. What the family says on the left is what the care expert
// is holding on the right, which is the whole argument of the product in one
// picture.
const heroTwoSurfaces = (
  <Frame {...FRAMES.heroWide} label={LABELS.heroWide} ground='custom.coral.100'>
    {/* The wedge. A flat triangle, so no gradient and nothing to soften. */}
    <Box
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        width: 300,
        height: 300,
        bgcolor: 'custom.sunflower.500',
        clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
      }}
    />

    <Layer x={500} y={70} width={660} behind>
      <Sheet>
        <HeaderBars />
        <Stack spacing={1.5}>
          {CLAIMS.map((claim) => (
            <ClaimRow
              key={claim.name}
              icon={claim.icon}
              name={claim.name}
              description={claim.description}
            />
          ))}
        </Stack>
      </Sheet>
    </Layer>

    <Layer x={80} y={150} width={480}>
      <Sheet>
        <Stack spacing={1.5}>
          {CONVERSATION.map((line) => (
            <ChatBubble key={line.text} from={line.from} text={line.text} />
          ))}
        </Stack>
      </Sheet>
    </Layer>

    {/* lift puts the ring on the card, not under it: the conversation card
        bottoms out at 425 and the pill's top is at 620, so 220 lands the ring
        25px inside the edge, the same overlap the layered group has.
        (Was 245 against a 400 bottom, before the copy in CONVERSATION changed
        and made the card taller.) */}
    <Callout x={150} y={620} lift={220}>
      {CALLOUTS.conversation}
    </Callout>
  </Frame>
);

/**
 * Reference 2: one surface, centred, generous margin, and one control lit.
 *
 * A function rather than three copies: the three feature pieces differ by their
 * ground and what is on the surface, and nothing else. Chloe wants these next
 * to body copy, so the surface stays inside the frame rather than bleeding.
 */
const featurePiece = (
  label: string,
  ground: string,
  body: React.ReactNode,
  /** How wide the surface sits. Content-shaped: two action rows stretched to
      the frame read as a table, and the claims want the room. */
  width: number = 1072,
) => (
  <Frame {...FRAMES.feature} label={label} ground={ground}>
    {/* Centred rather than placed: these three surfaces are different heights,
        and a fixed y left whichever was shortest sitting in a pool of ground. */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        px: 9,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Box sx={{ width }}>
        <Sheet>{body}</Sheet>
      </Box>
    </Box>
  </Frame>
);

const featureConversation = featurePiece(
  LABELS.featureConversation,
  'custom.gardenia',
  <>
    <Stack spacing={1.5}>
      {CONVERSATION.map((line) => (
        <ChatBubble key={line.text} from={line.from} text={line.text} />
      ))}
    </Stack>
    {/* The input, with the send control lit: this is the piece about answering
        in your own words, so the place you answer is what the eye should find. */}
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
      <Card variant='listItem' sx={{ flex: 1, px: 3, py: 2 }}>
        <Bar width={220} height={10} />
      </Card>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Highlight />
        <Box
          sx={{
            position: 'relative',
            width: 44,
            height: 44,
            bgcolor: 'custom.coral.500',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon icon={'paper-plane-top' as never} fontSize={16} />
        </Box>
      </Box>
    </Stack>
  </>,
);

// The phone, on brand ground. Fixed scale rather than PhoneFrame's fit-to-
// viewport: the frame is an export at a known size, so the device has to be the
// same size every time it is rendered. 403×840 of bezel at 0.78 leaves the
// margin the feature surfaces leave.
const callOnPhone = (
  <Frame {...FRAMES.device} label={LABELS.call} ground='custom.warmOrange.100'>
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <PhoneFrame scale={0.78}>
        <CallScreen />
      </PhoneFrame>
    </Box>
  </Frame>
);

// The same call with the app around it: the offer that starts a call, and the
// phone the call happens on. The argument is the sequence — you press Book a
// call in the app, and then you are on the phone to someone who already has
// your case open.
//
// Two fragments and nothing behind them. The profile sheet used to sit washed
// out across the middle and it read as a third thing to look at, which is one
// more than a picture with a sequence in it can carry.
//
// The offer is the app's own card and the app's own words, imported rather than
// retyped, so the picture cannot promise something the screen doesn't say.
const callWithApp = (
  <Frame
    {...FRAMES.heroWide}
    label={LABELS.callWithApp}
    ground='custom.warmOrange.100'
  >
    <Layer x={143} y={70} width={314}>
      <PhoneFrame scale={0.78}>
        <CallScreen />
      </PhoneFrame>
    </Layer>

    {/* A 40px gap off the phone's right edge, and held against the middle of it
        rather than level with its top: stacking their top edges made the two
        fragments read as two columns of the same thing. They overlapped for a
        version, which is how the other frames here separate their layers, and
        it made one object out of what is meant to be two moments. The pair is
        centred in the frame, so both margins are 143. */}
    <Layer x={497} y={250} width={560}>
      <OfferCard
        title={BOOK_CALL.title}
        subtitle={BOOK_CALL.description}
        primaryAction={{ label: BOOK_CALL.primary, onClick: () => {} }}
      />
    </Layer>

    {/* Outlined, and a long way under the card: this is the one callout in the
        set that lands near a button, and the filled pill was reading as a
        second button under the real one. Measured off a card that bottoms out
        at 440, so 145 lands the ring 25px inside its bottom edge. */}
    <Callout x={661} y={560} lift={145} quiet>
      {CALLOUTS.expert}
    </Callout>
  </Frame>
);

const featureActions = featurePiece(
  LABELS.featureActions,
  'custom.limeGreen.200',
  <Stack spacing={2}>
    {ACTIONS.map((action) => (
      <ActionCard
        key={action.id}
        action={action}
        onToggle={() => {}}
        onOpen={() => {}}
      />
    ))}
  </Stack>,
  760,
);

const featureClaims = featurePiece(
  LABELS.featureClaims,
  'custom.sunflower.200',
  <Stack spacing={1.5}>
    {CLAIMS.map((claim) => (
      <ClaimRow
        key={claim.name}
        icon={claim.icon}
        name={claim.name}
        description={claim.description}
        lead={claim.lead}
      />
    ))}
  </Stack>,
);

// The page. A column of frames at their export sizes, each one scrolling
// horizontally rather than shrinking: a frame that resized would not be the
// thing being exported.
const SiteMockups = () => (
  <Box sx={{ px: 4, py: 5, overflowX: 'auto' }}>
    <Stack sx={{ width: 'max-content' }} spacing={6}>
      {heroGroup}
      {heroTwoSurfaces}
      {featureConversation}
      {/* The two versions side by side rather than stacked: they are the same
          call, and the choice between them is a comparison. */}
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={6}>
        {callOnPhone}
        {callWithApp}
      </Stack>
      {featureActions}
      {featureClaims}
    </Stack>
  </Box>
);

export default SiteMockups;
